import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { OverviewDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import NodeModal from 'modules/job_catalog/components/NodeModal'
import useCreateTreeWithChildren from 'modules/job_catalog/hooks/useCreateTreeWithChildren'
import { useNodeModalContent } from 'modules/job_catalog/hooks/useModalContent'
import { useNodeColumns } from 'modules/job_catalog/hooks/useNodeColumns'
import {
  GroupedData,
  NODE_TYPES,
  NodeChildren,
  NodeFormValues,
  TreeNode,
  findNodeByUuid,
  formatUuidToNodeType,
  getChildNodeType,
  getChildTypeText,
  getNavigationForTypeFromFunction,
  getOverviewActionLabel,
  getOverviewEmptyStateContent,
  getSalariesFromChildAttributes,
  getValidNodeIdFromParams,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { formatSalariesFromCentsObject } from 'modules/job_catalog/lib/salaryUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const extractLevels = (nodes: TreeNode[]): string[] =>
  nodes.filter((n) => normalizeNodeType(n.type) === NODE_TYPES.LEVEL).map((n) => n.name)

const mapChildrenData = (
  childrenData: NodeChildren[],
  node: TreeNode | null,
  setNodeName: (name: string) => void
): GroupedData[] => {
  if (!node) {
    return []
  }

  setNodeName(node.name)
  const nodeType = normalizeNodeType(node.type)

  const mappedData = childrenData.map((child) => {
    const baseData = {
      uuid: `${child.type}-${child.id.toString()}`,
      name: child.name || '',
      salary:
        formatSalariesFromCentsObject(getSalariesFromChildAttributes(child.attributesJson)) || '-',
    }

    if (nodeType === NODE_TYPES.FUNCTION) {
      const roleNode = node.children.find((r) => r.name === child.name)
      return {
        ...baseData,
        levels: roleNode ? extractLevels(roleNode.children || []) : [],
      }
    }

    return baseData
  })

  return mappedData
}

const OverviewPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = getValidNodeIdFromParams(levelId, roleId, functionId, familyId, rootId)
  const [nodeName, setNodeName] = useState<string>('')
  const [nodeModalType, setNodeModalType] = useState<string | null>(null)
  const [nodeModalInitialValues, setNodeModalInitialValues] = useState<NodeFormValues | null>(null)
  const [totalChildren, setTotalChildren] = useState<number>(0)
  const childType = useMemo(() => getChildNodeType(id), [id])
  const { company } = useCurrentSession()
  const { createTree, loading: loadingCreateNode } = useCreateTreeWithChildren()
  const modalContent = useNodeModalContent(nodeModalType, nodeName, childType)

  const redirectParams = useMemo(() => {
    return (type: string, uuid: string) => ({
      rootId: rootId || '',
      familyId: familyId || '',
      functionId: functionId || '',
      roleId: roleId || '',
      levelId: levelId || '',
      ...(type === NODE_TYPES.FUNCTION && { functionId: uuid }),
      ...(type === NODE_TYPES.ROLE && { roleId: uuid }),
      ...(type === NODE_TYPES.LEVEL && { levelId: uuid }),
      ...(type === NODE_TYPES.FAMILY && { familyId: uuid }),
    })
  }, [rootId, familyId, functionId, roleId, levelId])

  const source = useGraphqlDataCollectionSource(
    {
      document: OverviewDataCollectionDocument,
      fetchPolicy: 'network-only',
      perPage: 10,
      totalItemSummary: (totalItems: number) => `${getChildTypeText(id, totalItems).toLowerCase()}`,
      mapData: (data) => {
        const rawTree = data?.jobCatalog.treesConnection.nodes[0]?.serializedTree ?? '[]'
        const childrenData = data?.jobCatalog.treeNodesConnection.nodes || []
        const parsed = JSON.parse(rawTree)
        const tree: TreeNode[] = Array.isArray(parsed) ? parsed : [parsed]
        const node = findNodeByUuid(tree, id)

        setTotalChildren(data?.jobCatalog.treeNodesConnection.totalCount ?? 0)
        const mappedChildrenData: NodeChildren[] = childrenData.map((child) => ({
          id: child.id,
          name: child.name || '',
          type: child.type || '',
          attributesJson: child.attributesJson || '[]',
        }))

        return mapChildrenData(mappedChildrenData, node, setNodeName)
      },
      mapPagination: (data) => ({
        total: data.jobCatalog.treeNodesConnection.totalCount ?? 0,
      }),
      search: {
        enabled: true,
      },
      sortings: {
        name: {
          label: getChildTypeText(id),
        },
      },
      defaultSortings: {
        field: 'name',
        order: 'asc',
      },
      itemUrl: (item: GroupedData) => {
        const type = normalizeNodeType(item.uuid?.split('-')[0] || '')
        const nav = getNavigationForTypeFromFunction(navigation, type)
        return nav.fullPath(redirectParams(type, item.uuid))
      },
      itemActions: (item) => [
        {
          label: 'Duplicate',
          onClick: () => {
            setNodeModalType('duplicate')
            setNodeModalInitialValues({
              name: item.name,
              description: '',
            })
          },
        },
      ],
      primaryActions: totalChildren
        ? () => ({
            label: getOverviewActionLabel(normalizeNodeType(childType || '')),
            onClick: () => setNodeModalType('create'),
            icon: Plus,
          })
        : undefined,
      mapVariables: ({ search, sortings }) => ({
        search,
        sortOrder: sortings?.[0],
        nodeUuid: id ?? '',
        childType: formatUuidToNodeType(childType || ''),
      }),
    },
    [id, childType]
  )

  const columns = useNodeColumns(String(id))

  useEffect(() => {
    source.setIsLoading(loadingCreateNode)
  }, [loadingCreateNode, source])

  const handleCreateNode = async ({ name, description, children }: NodeFormValues) => {
    const result = await createTree({
      name,
      description,
      children,
      parentUuid: id,
      nodeType: formatUuidToNodeType(childType || ''),
      companyId: company.id,
    })

    if (result.success && result.newNodeUuid) {
      const normalized = normalizeNodeType(childType)
      const nav = getNavigationForTypeFromFunction(navigation, normalized)
      navigate(nav.fullPath(redirectParams(normalized, result.newNodeUuid)))
      setNodeModalType(null)
    }
  }

  return (
    <ResponsivePage.Body variant="full">
      <OneDataCollection
        key={`overview-${id}`}
        source={source}
        emptyStates={{
          'no-data': getOverviewEmptyStateContent({
            type: normalizeNodeType(childType || ''),
            handleAction: () => setNodeModalType('create'),
          }),
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns,
              frozenColumns: columns.length > 1 ? 1 : 0,
            },
          },
        ]}
      />
      {nodeModalType && (
        <NodeModal
          onClose={() => setNodeModalType(null)}
          title={modalContent.title}
          description={modalContent.description}
          onSubmit={handleCreateNode}
          icon={modalContent.icon || 'input-field'}
          nodeType={childType || ''}
          initialValues={nodeModalInitialValues ?? undefined}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default OverviewPage
